import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import transactionService from '../services/transactionService';

export const fetchTransactions = createAsyncThunk(
  'transactions/fetch',
  async (_, { rejectWithValue }) => {
    try {
      const response = await transactionService.getAll();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const addTransaction = createAsyncThunk(
  'transactions/add',
  async (transactionData, { rejectWithValue }) => {
    try {
      const response = await transactionService.create(transactionData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// 3 sep 2026: crea una transferencia (egreso en cuenta origen + ingreso
// en cuenta destino, ver POST /transactions/transferencia) -- devuelve
// {origen, destino}, las dos Transaction ya creadas.
export const addTransferencia = createAsyncThunk(
  'transactions/addTransferencia',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await transactionService.crearTransferencia(payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateTransaction = createAsyncThunk(
  'transactions/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await transactionService.update(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteTransaction = createAsyncThunk(
  'transactions/delete',
  async (id, { rejectWithValue }) => {
    try {
      await transactionService.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const addMultipleTransactions = createAsyncThunk(
  'transactions/addMultiple',
  async (transactionsData, { rejectWithValue }) => {
    try {
      // Podríamos optimizar esto para hacerlo en una sola llamada al backend
      // pero por ahora usamos las llamadas individuales existentes
      const results = [];
      for (const transaction of transactionsData) {
        const response = await transactionService.create(transaction);
        results.push(response.data);
      }
      return results;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

const initialState = {
  list: [],
  loading: false,
  error: null,
  summary: {
    totalIncome: 0,
    totalExpenses: 0,
    netBalance: 0,
  },
};

const transactionSlice = createSlice({
  name: 'transactions',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch transactions
    builder
      .addCase(fetchTransactions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransactions.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload;
        state.summary = calculateSummary(action.payload);
      })
      .addCase(fetchTransactions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Add transaction
    builder
      .addCase(addTransaction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addTransaction.fulfilled, (state, action) => {
        state.loading = false;
        state.list.push(action.payload);
        // FIX: mismo bug que se encontró en debtSlice — state.list ya
        // incluye action.payload por el push de arriba, así que sumarlo
        // otra vez en el array pasado a calculateSummary lo contaba doble.
        state.summary = calculateSummary(state.list);
      })
      .addCase(addTransaction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Add transferencia (dos Transaction a la vez)
    builder
      .addCase(addTransferencia.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addTransferencia.fulfilled, (state, action) => {
        state.loading = false;
        state.list.push(action.payload.origen, action.payload.destino);
        state.summary = calculateSummary(state.list);
      })
      .addCase(addTransferencia.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Update transaction
    builder
      .addCase(updateTransaction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTransaction.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.list.findIndex(t => t.id === action.payload.id);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
        state.summary = calculateSummary(state.list);
      })
      .addCase(updateTransaction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Delete transaction
    builder
      .addCase(deleteTransaction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTransaction.fulfilled, (state, action) => {
        state.loading = false;
        state.list = state.list.filter(t => t.id !== action.payload);
        state.summary = calculateSummary(state.list);
      })
      .addCase(deleteTransaction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
    // Add multiple transactions (import)
      .addCase(addMultipleTransactions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addMultipleTransactions.fulfilled, (state, action) => {
        state.loading = false;
        state.list = [...state.list, ...action.payload];
        state.summary = calculateSummary(state.list);
      })
      .addCase(addMultipleTransactions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

// FIX: filtraba por t.transaction_type ('income'/'expense') y sumaba
// t.amount — campos en inglés que no existen en la respuesta real del
// backend (que usa tipo: 'ingreso'/'gasto' y monto). El resultado siempre
// era 0 en ambos totales.
const calculateSummary = (transactions) => {
  const income = transactions
    .filter(t => t.tipo === 'ingreso')
    .reduce((sum, t) => sum + (t.monto || 0), 0);

  const expenses = transactions
    .filter(t => t.tipo === 'gasto')
    .reduce((sum, t) => sum + (t.monto || 0), 0);

  return {
    totalIncome: income,
    totalExpenses: expenses,
    netBalance: income - expenses,
  };
};

export const { clearError } = transactionSlice.actions;
export default transactionSlice.reducer;
